/**
 * Chamadas HTTP dos colaboradores.
 *
 * Papel: aqui ficam as quatro chamadas de `/api/colaboradores` que a tela usa —
 * listar, criar, alterar e excluir um colaborador.
 *
 * Como usar: a listagem devolve o array inteiro, e não uma página. Nenhuma
 * transformação acontece aqui — o que sai é o que o schema validou, inclusive o
 * CPF já reduzido a dígitos.
 */
import { type Colaborador, type NovoColaborador, colaboradorSchema } from '@concierge/shared';
import { z } from 'zod';

import { clienteApi } from '@/shared/api';

/** A listagem inteira, sem envelope de paginação. */
const listaDeColaboradoresSchema = z.array(colaboradorSchema);

/**
 * Lê todos os colaboradores, em ordem alfabética de nome completo.
 *
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns Os colaboradores, cada um com a residência aninhada.
 */
export function buscarColaboradores(sinal?: AbortSignal): Promise<Colaborador[]> {
  return clienteApi.get('/colaboradores', { schema: listaDeColaboradoresSchema, sinal });
}

/**
 * Cria um colaborador.
 *
 * A API responde 409 em três casos que o formulário distingue pela mensagem: o
 * CPF já cadastrado, o nome completo já cadastrado, e a residência escolhida que
 * deixou de existir enquanto o formulário estava aberto.
 *
 * @param dados - O colaborador preenchido, já validado.
 * @returns O colaborador como ficou gravado, com a residência aninhada.
 */
export function criarColaborador(dados: NovoColaborador): Promise<Colaborador> {
  return clienteApi.post('/colaboradores', dados, { schema: colaboradorSchema });
}

/**
 * Substitui todos os campos de um colaborador.
 *
 * @param id - Identificador da linha em `colaboradores`.
 * @param dados - O colaborador inteiro, e não apenas os campos alterados.
 * @returns O colaborador como ficou gravado.
 */
export function atualizarColaborador(
  id: string,
  dados: NovoColaborador,
): Promise<Colaborador> {
  return clienteApi.put(`/colaboradores/${encodeURIComponent(id)}`, dados, {
    schema: colaboradorSchema,
  });
}

/**
 * Apaga um colaborador.
 *
 * Não recebe schema porque a API responde 204 sem corpo. A API responde 409
 * quando o colaborador ainda tem banco de horas, e nesse caso nada é apagado —
 * histórico de horas é registro trabalhista.
 *
 * @param id - Identificador da linha em `colaboradores`.
 */
export function excluirColaborador(id: string): Promise<void> {
  return clienteApi.remover(`/colaboradores/${encodeURIComponent(id)}`);
}
